import type { KeyboardEvent, RefObject } from 'react';
import { Bot, Check, Loader2, Pencil, X } from 'lucide-react';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ActorAvatar } from '@/components/roros/ActorAvatar';
import type { RorosActorDTO } from '@/types/roros/actors';
import type { AgentSessionDTO } from '@/services/roros/agent-sessions.service';
import { ArtifactsBadgeButton } from './ArtifactsSheet';
import { StatusChip } from './AgentChatPageParts';

type AgentChatTab = 'chat' | 'inspector';

interface AgentChatHeaderProps {
  activeSession: AgentSessionDTO;
  activeActor?: RorosActorDTO;
  actorName: string;
  artifactsCount: number;
  onOpenArtifacts: () => void;
  activeTab: AgentChatTab;
  onActiveTabChange: (tab: AgentChatTab) => void;
  isAdmin: boolean;
  isTitleEditing: boolean;
  titleDraft: string;
  onTitleDraftChange: (value: string) => void;
  titleInputRef: RefObject<HTMLInputElement | null>;
  onTitleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onSaveTitle: () => void;
  isTitleSaving: boolean;
  onStartTitleEdit: () => void;
  onCancelTitleEdit: () => void;
  onCancelSession: () => void;
}

export function AgentChatHeader({
  activeSession,
  activeActor,
  actorName,
  artifactsCount,
  onOpenArtifacts,
  activeTab,
  onActiveTabChange,
  isAdmin,
  isTitleEditing,
  titleDraft,
  onTitleDraftChange,
  titleInputRef,
  onTitleKeyDown,
  onSaveTitle,
  isTitleSaving,
  onStartTitleEdit,
  onCancelTitleEdit,
  onCancelSession,
}: AgentChatHeaderProps) {
  return (
    <div className="flex items-start justify-between pb-2 border-b border-slate-100 mb-2">
      <div className="flex items-start gap-2 min-w-0 flex-1">
        {/* AC-CHAT-12 (fix): operator avatar — use the real actor when present. */}
        {activeActor ? (
          <div data-testid="chat-header-avatar">
            <ActorAvatar actor={activeActor} size="sm" />
          </div>
        ) : (
          <div
            className="h-8 w-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0"
            data-testid="chat-header-avatar"
          >
            <Bot className="h-4 w-4" aria-hidden />
          </div>
        )}

        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          {/* Operator name — top line */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-800 truncate">
              {actorName}
            </span>
            <StatusChip status={activeSession.status ?? 'idle'} />
            {/* AC-M5-3: artifacts badge */}
            <ArtifactsBadgeButton
              sessionId={activeSession.id}
              count={artifactsCount}
              onClick={onOpenArtifacts}
            />
            {/* AC-M7-6: tab buttons */}
            <div className="flex items-center gap-1" data-testid="session-tabs">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onActiveTabChange('chat')}
                className={`text-xs px-2 py-0.5 h-auto ${activeTab === 'chat' ? 'text-blue-700 bg-blue-50 font-medium' : 'text-slate-500'}`}
                data-testid="tab-chat"
              >
                {t('Chat')}
              </Button>
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onActiveTabChange('inspector')}
                  className={`text-xs px-2 py-0.5 h-auto ${activeTab === 'inspector' ? 'text-blue-700 bg-blue-50 font-medium' : 'text-slate-500'}`}
                  data-testid="tab-inspector"
                >
                  {t('Inspector')}
                </Button>
              )}
            </div>
          </div>

          {/* AC-CHAT-10/12: Editable session title — second line */}
          {isTitleEditing ? (
            <div className="flex items-center gap-1">
              <Input
                ref={titleInputRef}
                value={titleDraft}
                onChange={(e) => onTitleDraftChange(e.target.value)}
                onKeyDown={onTitleKeyDown}
                className="h-6 text-xs py-0 px-2 w-48"
                autoFocus
                data-testid="session-title-input"
              />
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-green-600 hover:text-green-800 hover:bg-green-50"
                onClick={onSaveTitle}
                disabled={isTitleSaving}
                data-testid="session-title-save"
                aria-label={t('Save title')}
              >
                {isTitleSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                onClick={onCancelTitleEdit}
                data-testid="session-title-cancel-edit"
                aria-label={t('Cancel')}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1 group/title">
              <span
                className="text-xs text-slate-500 truncate cursor-pointer hover:text-slate-700"
                data-testid="session-title"
                onClick={onStartTitleEdit}
                title={t('Click to rename')}
              >
                {activeSession.title ?? t('Agent Chat')}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 opacity-0 group-hover/title:opacity-100 text-slate-400 hover:text-slate-600 transition-opacity"
                onClick={onStartTitleEdit}
                data-testid="session-title-edit"
                aria-label={t('Rename session')}
              >
                <Pencil className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {(activeSession.status === 'running' || activeSession.status === 'idle') && activeTab === 'chat' && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancelSession}
          className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
          data-testid="cancel-session-button"
        >
          <X className="h-4 w-4 mr-1" />
          {t('Cancel')}
        </Button>
      )}
    </div>
  );
}
